import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Info } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Renders a TutorBird script-based widget inside an isolated iframe (srcdoc),
 * so any cross-origin script errors don't leak into the parent React app.
 * The iframe auto-resizes to fit its content.
 */
export default function TutorBirdWidget({ src, fallbackUrl, title = 'TutorBird Widget', minHeight = 560 }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(minHeight);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!src || !iframeRef.current) return;

    const iframe = iframeRef.current;

    // Build HTML that loads the TutorBird script
    const html = `<!doctype html><html><head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<style>
  html, body { margin:0; padding:0; background:transparent;
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color:#0f172a;
  }
  body { padding: 8px; }
  /* Hide scrollbar inside */
  ::-webkit-scrollbar { width: 0; height: 0; }
  /* Improve TutorBird widget integration */
  a { color: #0d9488; }
  button, input[type="submit"] { border-radius: 9999px !important; }
</style>
</head><body>
<div id="tb-widget"></div>
<script src="${src}"><\/script>
<script>
  // Auto-resize: notify parent when content height changes
  function postSize() {
    var h = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight
    );
    parent.postMessage({ type: 'tb-resize', height: h }, '*');
  }
  window.addEventListener('load', function(){ setTimeout(postSize, 200); setTimeout(postSize, 800); setTimeout(postSize, 1500); });
  // Observe DOM changes
  var obs = new MutationObserver(function(){ postSize(); });
  obs.observe(document.body, { childList:true, subtree:true, attributes:true });
  // Silence harmless cross-origin errors
  window.addEventListener('error', function(e){ e.stopPropagation && e.stopPropagation(); }, true);
<\/script>
</body></html>`;

    iframe.srcdoc = html;
    setLoading(true);

    const onMessage = (event) => {
      const data = event.data;
      if (data && data.type === 'tb-resize' && typeof data.height === 'number') {
        setHeight(Math.max(minHeight, data.height + 24));
      }
    };
    window.addEventListener('message', onMessage);
    const t = setTimeout(() => setLoading(false), 1500);
    return () => {
      window.removeEventListener('message', onMessage);
      clearTimeout(t);
    };
  }, [src, minHeight]);

  if (!src) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-teal-200 bg-teal-50/40 p-10 text-center">
        <Info className="h-10 w-10 text-teal-500 mx-auto" />
        <h3 className="mt-4 font-bold text-slate-900">Widget bald verfügbar</h3>
        <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
          Dieses Widget ist noch nicht konfiguriert. Du kannst direkt zum TutorBird-Portal wechseln.
        </p>
        {fallbackUrl && (
          <a href={fallbackUrl} target="_blank" rel="noreferrer">
            <Button className="btn-teal rounded-full mt-5 h-11 px-6">
              Zum Portal <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl bg-white border border-slate-100 overflow-hidden">
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10" style={{ minHeight }}>
          <div className="h-10 w-10 rounded-full border-4 border-teal-200 border-t-teal-500 animate-spin" />
          <p className="mt-4 text-sm text-slate-500">Lade Widget...</p>
        </div>
      )}
      <iframe
        ref={iframeRef}
        title={title}
        onLoad={() => setLoading(false)}
        style={{ width: '100%', height: `${height}px`, border: 0, display: 'block' }}
        sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-storage-access-by-user-activation"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}
